import React, { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useParams } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { pageURLs } from "@/constants";
import { fetchUserStatistics } from "../../../../actions/userprofile";
import { USER_TYPE_AGENT, BUY, LONG_RENT } from "../../../../constants/common";
import { getAddressDisplay } from "../../../../helpers/common";
import { formatCompactPrice } from "../../../../helpers/priceCapUtils";
import PremiumPlanModal from "../../../PlanUpgrade/PremiumPlanModal";
import BasicPlanModal from "../../../PlanUpgrade/BasicPlanModal";
import "./property_dropdown_list.scss";
import { useMLSAgentAtGitchaRedirect } from "@/hooks/useMLSAgentAtGitchaRedirect";
import useAiSyncStatus from "@/hooks/useAiSyncStatus";

const MIN_OVERLAY_WIDTH = 430;
const VIEWPORT_GUTTER = 12;
const MOBILE_BREAKPOINT = 768;

// The rail (.left-panel) is an overflow:auto, position:relative scroll
// container, so it is both the clipping box and the containing block for any
// positioned descendant. An in-flow list widened it and made the rail scroll
// sideways; an absolutely positioned one would simply be cut off at the rail's
// edge. Neither can sit over the feed, so the list is portalled to body and
// positioned against the button's viewport rect.
const overlayPlacement = (anchor) => {
  if (!anchor) return { style: { visibility: "hidden" }, mobile: false };

  const vw = window.innerWidth;
  const vh = window.innerHeight;

  if (vw < MOBILE_BREAKPOINT) return { style: null, mobile: true };

  const width = Math.min(
    Math.max(anchor.width, MIN_OVERLAY_WIDTH),
    vw - VIEWPORT_GUTTER * 2,
  );
  // Prefer left-aligned to the button; pull back in if that would run off.
  const left = Math.max(
    VIEWPORT_GUTTER,
    Math.min(anchor.left, vw - width - VIEWPORT_GUTTER),
  );

  const below = vh - anchor.bottom - VIEWPORT_GUTTER;
  const above = anchor.top - VIEWPORT_GUTTER;
  const flip = below < 260 && above > below;

  return {
    mobile: false,
    style: flip
      ? { left, width, bottom: vh - anchor.top + 4, maxHeight: Math.min(520, above) }
      : { left, width, top: anchor.bottom + 4, maxHeight: Math.min(520, below) },
  };
};

const PropertyDropdownList = ({
  anchor,
  onRequestClose,
  propertyList,
  selectedProperty,
  setSelectedProperty,
  adType,
  userType,
  selectedClient,
  userDetails,
  updateReloadParam,
}) => {
  const navigate = useNavigate();
  const params = useParams();
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState("");
  const [showPremiumPlanModal, setPremiumPlanModal] = useState(false);
  const [showBasicPlanModal, setBasicPlanModal] = useState(false);

  const userStatictics = useSelector(
    (state) => state?.userProfile?.userStatictics,
  );
  const userPrivileges = useSelector(
    (state) => state?.userProfile?.userPrivileges,
  );
  const [userCount, setUserCount] = useState({});
  const [allowedCount, setAllowedCount] = useState({});

  useEffect(() => {
    if (userDetails?.User_Plan_Id) {
      dispatch(fetchUserStatistics({ UserPlanId: userDetails.User_Plan_Id }));
    }
  }, [userDetails]);

  useEffect(() => {
    if (userPrivileges?.length > 0) setAllowedCount(userPrivileges[0]);
  }, [userPrivileges]);

  useEffect(() => {
    if (userStatictics?.length > 0) setUserCount(userStatictics[0]);
  }, [userStatictics]);

  // Document-level, not onKeyDown on the portal root: the root only sees
  // Escape if focus is already inside it, which fails whenever the search
  // input isn't rendered (propertyList.length <= 3) - focus is still on the
  // trigger button, a separate DOM subtree the portal's bubbling never reaches.
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onRequestClose?.();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onRequestClose]);

  const { redirectToBLSEnv } = useMLSAgentAtGitchaRedirect()
  // Shares its query key with the Feed banner, so what this list offers and
  // what the banner says can never disagree.
  const aiSync = useAiSyncStatus();

  const getPrice = (property) => {
    if (adType === BUY) return property.AskingPrice_Sell;
    if (adType === LONG_RENT) return property.AskingPrice_Rent;
    return property.AskingPrice_Night;
  };

  const filtered = useMemo(
    () =>
      propertyList.filter((p) => {
        if (!searchTerm.trim()) return true;
        const term = searchTerm.toLowerCase();
        const loc = (p.Location || "").toLowerCase();
        const city = (p.City || "").toLowerCase();
        const state = (p.State || "").toLowerCase();
        return (
          loc.includes(term) || city.includes(term) || state.includes(term)
        );
      }),
    [propertyList, searchTerm],
  );

  // Both row types already carry potentialBuyers - the inventory list computes
  // it in SQL and the MLS list correlates it - so the count was available all
  // along and simply never rendered. Grouping uses rowType, which the MLS
  // adapter already sets.
  const matchCount = (p) => Number(p?.potentialBuyers) || 0;

  const groups = useMemo(
    () =>
      [
        {
          key: "mls",
          label: "My active MLS listings",
          items: filtered.filter((p) => p.rowType === "mls"),
        },
        {
          key: "prospect",
          label: "Prospect properties",
          items: filtered.filter((p) => p.rowType !== "mls"),
        },
      ]
        .map((g) => ({
          ...g,
          // Most demand first - the reason to open this list at all.
          items: [...g.items].sort((a, b) => matchCount(b) - matchCount(a)),
        }))
        .filter((g) => g.items.length > 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filtered],
  );

  const handleAddProperty = () => {
    redirectToBLSEnv({
      path: `${pageURLs.agentCreateInventory}`,
      onGitchaEnvFallback: () => {
        if (userCount?.Inventory >= allowedCount?.Inventory) {
          if (userType === USER_TYPE_AGENT) {
            setPremiumPlanModal(true);
          } else {
            setBasicPlanModal(true);
          }
        } else {
          if (userType === USER_TYPE_AGENT) {
            navigate(pageURLs.agentCreateInventory, {
              state: {
                data: {
                  wantAdUuid: params.wantAdId,
                  clientId: selectedClient?.value,
                  clientName: selectedClient?.label,
                  isAgentAsSeller: selectedClient?.AgentAsSeller,
                },
              },
            });
          } else {
            navigate(pageURLs.memberCreateInventory);
          }
        }
      }
    })
  };

  const placement = overlayPlacement(anchor);

  return createPortal(
    <div
      className={`property-dropdown-overlay${placement.mobile ? " property-dropdown-overlay--sheet" : " property-dropdown-overlay--floating"}`}
      style={placement.style || undefined}
      role="listbox"
    >
      {propertyList.length > 3 && (
        <div className="property-dropdown-overlay__search">
          <input
            type="text"
            placeholder="Search properties..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            autoFocus
          />
        </div>
      )}

      {/* A newly onboarded agent's properties are held back entirely until
          their matches exist — the selector is where the dead end would
          otherwise start. */}
      {aiSync.isGated ? (
        <div className="property-dropdown-overlay__syncing" role="status">
          <span className="property-dropdown-overlay__syncing-spark" aria-hidden="true">
            ✦
          </span>
          <strong>Syncing your listings and finding buyer matches</strong>
          <span>
            Your listings appear here as soon as their buyer matches are ready
            {aiSync.total > 0 ? ` (${aiSync.settled} of ${aiSync.total})` : ""}.
          </span>
        </div>
      ) : (
      <div className="property-dropdown-overlay__list">
        {groups.map((group) => (
          // role="group", not another listbox child: a listbox's direct
          // children must be options, so the group label lives in
          // aria-label rather than as a sibling node inside the listbox.
          <div
            className="property-dropdown-group"
            key={group.key}
            role="group"
            aria-label={group.label}
          >
            <div className="property-dropdown-group__label" aria-hidden="true">
              <span>{group.label}</span>
              <span className="property-dropdown-group__count">
                {group.items.length}
              </span>
            </div>
            {group.items.map((property) => {
              const isSelected =
                selectedProperty?.Inv_Id === property.Inv_Id;
              const addr = getAddressDisplay(
                property.Location,
                property.City,
                property.State,
                property.ZIP,
              );
              const price = getPrice(property);
              const matches = matchCount(property);
              const unranked = property?.potentialBuyers == null;

              return (
                <button
                  key={property.Inv_Id}
                  className={`property-dropdown-item ${isSelected ? "property-dropdown-item--selected" : ""}`}
                  onClick={() => setSelectedProperty(property)}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                >
                  {/* Only synced MLS listings carry photos - a prospect's
                      property has no image field at all, so the fallback is a
                      deliberate mark rather than a placeholder that reads as a
                      failed load. */}
                  <div
                    className={`property-dropdown-item__thumb${
                      property.photo ? "" : " property-dropdown-item__thumb--none"
                    }`}
                  >
                    {property.photo ? (
                      <img src={property.photo} alt="" />
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path
                          d="M3 10.5 12 4l9 6.5V20a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1v-9.5Z"
                          stroke="currentColor"
                          strokeWidth="1.6"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </div>
                  <div className="property-dropdown-item__info">
                    <span className="property-dropdown-item__addr">
                      {addr.before || property.Location}
                    </span>
                    {addr.after && (
                      <span className="property-dropdown-item__city">
                        {addr.after}
                      </span>
                    )}
                  </div>
                  <div className="property-dropdown-item__right">
                    {/* Borrows only the COLOUR class from the dashboard tag.
                        The global .want-ad-tag also carries margin-bottom:31px
                        for the dashboard table, which threw this out of line
                        and made it look like it was overlapping the address. */}
                    <span
                      className={`property-dropdown-item__tag ${
                        matches > 0
                          ? "medium-blue-tag-bg-border"
                          : "property-dropdown-item__tag--none"
                      }`}
                    >
                      {/* "None yet" read as not-scored-yet; these ARE scored
                          and the answer is zero. "—" is the only unscored
                          state. */}
                      {unranked
                        ? "—"
                        : `${matches} ${matches === 1 ? "Match" : "Matches"}`}
                    </span>
                    {price > 0 && (
                      <span className="property-dropdown-item__price">
                        {formatCompactPrice(price)}
                      </span>
                    )}
                    {isSelected && (
                      <span className="property-dropdown-item__check">
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                          <path
                            d="M2.5 7L5.5 10L11.5 4"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        ))}
        {filtered.length === 0 && (
          <p className="property-dropdown-overlay__empty">
            {searchTerm.trim()
              ? "No properties match your search"
              : "No properties yet"}
          </p>
        )}
      </div>
      )}

      <div className="property-dropdown-overlay__footer">
        <button
          className="btn btn-secondary w-100"
          onClick={handleAddProperty}
          type="button"
        >
          Save Property
        </button>
      </div>

      <PremiumPlanModal
        userDetails={userDetails}
        updateReloadParam={updateReloadParam}
        show={showPremiumPlanModal}
        onHide={() => setPremiumPlanModal(false)}
        fromInventoryListing={true}
        showPlanModal={() => setPremiumPlanModal(true)}
      />
      <BasicPlanModal
        userDetails={userDetails}
        show={showBasicPlanModal}
        updateReloadParam={updateReloadParam}
        onHide={() => setBasicPlanModal(false)}
        fromInventoryListing={true}
        showPlanModal={() => setBasicPlanModal(true)}
      />
    </div>,
    document.body,
  );
};

export default PropertyDropdownList;
